import { useContext, useEffect, useState } from "react";
import { useTabStore, useFiltersStore } from "~/stores";
import useFetchFilterData from "./useFetchFilterData";
import { AdvancedSearchContext } from "~/providers/AdvancedSearchProvider";
import { LanguageContext } from "~/providers/LanguageProvider";
import { SEARCH_TABS } from "~/types/search.types";
import { FilterType } from "~/types/filters.types";
import { FilterActions } from "~/types/actions.enum";

const useFilters = () => {
    const {
        disclosureFilters,
        yearFilters,
        industryFilters,
        imageThemeFilters,
        pageSectionFilters,
        companyFilters,
        searchTerm,
        setYearFilters,
        setIndustryFilters,
        setCompanyFilters
    } = useContext(AdvancedSearchContext);
    const { language } = useContext(LanguageContext);

    const [initLoad, setInitLoad] = useState(true);
    const { fetchFilterOptions } = useFetchFilterData();

    // Store methods
    const dispatch = useFiltersStore(state => state.dispatch);
    const yearFilterOptions = useFiltersStore(state => state.yearFilters);
    const activeTab = useTabStore(state => state.tab);
    const isFullDocumentsTab = activeTab?.key === SEARCH_TABS.FULL_DOCUMENTS;

    const resetFilterOptions = () => {
        setInitLoad(true);
        setYearFilters([]);
        if (!isFullDocumentsTab) {
            setIndustryFilters([]);
            setCompanyFilters([]);
        }
        dispatch({ type: FilterActions.SET_YEAR_FILTERS, payload: [] });
    };

    const updateYearOptions = async () => {
        resetFilterOptions();
        dispatch({ type: FilterActions.SET_IS_LOADING_YEARS, payload: true });
        if (!isFullDocumentsTab) {
            dispatch({ type: FilterActions.SET_IS_LOADING_INDUSTRIES, payload: true });
            dispatch({ type: FilterActions.SET_IS_LOADING_COMPANIES, payload: true });
        }
        await fetchFilterOptions([FilterType.YEAR]);
    };

    const updateIndustryOptions = () => {
        dispatch({ type: FilterActions.SET_IS_LOADING_INDUSTRIES, payload: true });
        fetchFilterOptions([FilterType.INDUSTRY, FilterType.COMPANY]);

        if (industryFilters.length > 0) {
            setIndustryFilters([]);
        }

        if (companyFilters.length > 0) {
            setCompanyFilters([]);
        }
    };

    const updateCompanyOptions = () => {
        dispatch({ type: FilterActions.SET_IS_LOADING_COMPANIES, payload: true });
        fetchFilterOptions([FilterType.COMPANY]);
        setCompanyFilters([]);
    };

    // YEAR filter options update on topic selection
    useEffect(() => {
        if (
            !isFullDocumentsTab &&
            (imageThemeFilters.length > 0 || pageSectionFilters.length > 0)
        ) {
            updateYearOptions();
        }
    }, [imageThemeFilters, pageSectionFilters, language]);

    // YEAR filter options update on keyword search
    useEffect(() => {
        if (!isFullDocumentsTab && searchTerm) {
            updateYearOptions();
        }
    }, [searchTerm, language]);

    // YEAR filter options update for full documents
    useEffect(() => {
        if (isFullDocumentsTab && companyFilters.length > 0) {
            updateYearOptions();
        }
    }, [companyFilters, disclosureFilters, language]);

    // Set the 1st 2 most recent years as default
    useEffect(() => {
        if (initLoad && yearFilterOptions.length > 0 && yearFilters.length === 0) {
            const defaultYears = yearFilterOptions.slice(0, 2);
            setYearFilters(defaultYears);
            setInitLoad(false);
        }
    }, [yearFilterOptions, initLoad]);

    // INDUSTRY filter options update on year selection change
    useEffect(() => {
        if (!initLoad && !isFullDocumentsTab) {
            updateIndustryOptions();
        }
    }, [yearFilters, isFullDocumentsTab, initLoad]);

    // COMPANY filter options update on industry selection change
    useEffect(() => {
        if (!initLoad && !isFullDocumentsTab) {
            updateCompanyOptions();
        }
    }, [industryFilters, isFullDocumentsTab, initLoad]);
};

export default useFilters;
